/**
 * Sweep kernel — extrude profile regions along an open 3D path.
 *
 * Frames: parallel transport (rotation-minimizing) along the path polyline,
 * seeded with the profile's own plane orientation when the path starts
 * perpendicular to it, else a least-parallel world axis. Profile UV coords
 * are offsets in each frame, so a profile drawn around its sketch origin
 * sweeps centered on the path. Earcut caps close both ends.
 */

import earcut from 'earcut';

import { ensureOutward } from './meshUtils';
import type { MeshData, ProfileRegion } from './types';
import type { Vec2 } from '../timeline/types';

type V3 = [number, number, number];

const EPS = 1e-9;

export function sweepProfiles(
  regions: readonly ProfileRegion[],
  path: readonly V3[],
): MeshData {
  if (path.length < 2) throw new Error('Sweep path needs at least two points');
  const pts = dedupe(path);
  if (pts.length < 2) throw new Error('Sweep path is degenerate');

  const frames = transportFrames(pts);
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  const edges: number[] = [];

  for (const region of regions) {
    sweepRegion(region, pts, frames, positions, normals, indices, edges);
  }
  return ensureOutward({
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    indices: new Uint32Array(indices),
    edgePositions: new Float32Array(edges),
  });
}

function dedupe(path: readonly V3[]): V3[] {
  const out: V3[] = [path[0]!];
  for (const p of path) {
    const last = out[out.length - 1]!;
    if (Math.hypot(p[0] - last[0], p[1] - last[1], p[2] - last[2]) > 1e-6) out.push(p);
  }
  return out;
}

interface Frame {
  origin: V3;
  x: V3;
  y: V3;
}

/** Rotation-minimizing frames via the double-reflection-free simple variant. */
function transportFrames(pts: readonly V3[]): Frame[] {
  const tangent = (i: number): V3 => {
    const a = pts[Math.max(0, i - 1)]!;
    const b = pts[Math.min(pts.length - 1, i + 1)]!;
    return norm([b[0] - a[0], b[1] - a[1], b[2] - a[2]]);
  };

  const t0 = tangent(0);
  // Seed X: world axis least parallel to the start tangent.
  const seed: V3 = Math.abs(t0[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  let x = norm(cross(cross(t0, seed), t0));
  let prevT = t0;

  const frames: Frame[] = [];
  for (let i = 0; i < pts.length; i++) {
    const t = tangent(i);
    // Rotate x by the minimal rotation taking prevT → t.
    const axis = cross(prevT, t);
    const s = Math.hypot(...axis);
    const c = dot(prevT, t);
    if (s > EPS) {
      x = rotateAbout(x, norm(axis), Math.atan2(s, c));
    }
    x = norm(cross(cross(t, x), t)); // re-orthogonalize against drift
    frames.push({ origin: pts[i]!, x, y: cross(t, x) });
    prevT = t;
  }
  return frames;
}

function sweepRegion(
  region: ProfileRegion,
  pts: readonly V3[],
  frames: readonly Frame[],
  positions: number[],
  normals: number[],
  indices: number[],
  edges: number[],
): void {
  const place = (f: Frame, p: Vec2): V3 => [
    f.origin[0] + p[0] * f.x[0] + p[1] * f.y[0],
    f.origin[1] + p[0] * f.x[1] + p[1] * f.y[1],
    f.origin[2] + p[0] * f.x[2] + p[1] * f.y[2],
  ];

  const loops = [region.outer, ...region.holes];

  // Walls.
  for (const loop of loops) {
    for (let k = 0; k < loop.length; k++) {
      const p0 = loop[k]!;
      const p1 = loop[(k + 1) % loop.length]!;
      for (let j = 0; j + 1 < frames.length; j++) {
        const a = place(frames[j]!, p0);
        const b = place(frames[j]!, p1);
        const c = place(frames[j + 1]!, p1);
        const d = place(frames[j + 1]!, p0);
        pushQuad(a, b, c, d, positions, normals, indices);
      }
      // Corner rails as display edges.
      const p2 = loop[(k + 2) % loop.length]!;
      if (isSharp(p0, p1, p2)) {
        for (let j = 0; j + 1 < frames.length; j++) {
          edges.push(...place(frames[j]!, p1), ...place(frames[j + 1]!, p1));
        }
      }
    }
  }

  // End caps.
  const flat: number[] = [];
  const holeStarts: number[] = [];
  for (const p of region.outer) flat.push(p[0], p[1]);
  for (const hole of region.holes) {
    holeStarts.push(flat.length / 2);
    for (const p of hole) flat.push(p[0], p[1]);
  }
  const tris = earcut(flat, holeStarts.length ? holeStarts : undefined);
  const capVerts: Vec2[] = [];
  for (let i = 0; i < flat.length; i += 2) capVerts.push([flat[i]!, flat[i + 1]!]);

  for (const [frame, reverse] of [
    [frames[0]!, true], // start cap faces backward
    [frames[frames.length - 1]!, false],
  ] as const) {
    const t = cross(frame.x, frame.y); // frame tangent (z)
    const sign = reverse ? -1 : 1;
    const base = positions.length / 3;
    for (const p of capVerts) {
      positions.push(...place(frame, p));
      normals.push(t[0] * sign, t[1] * sign, t[2] * sign);
    }
    for (let i = 0; i < tris.length; i += 3) {
      if (reverse) indices.push(base + tris[i]!, base + tris[i + 2]!, base + tris[i + 1]!);
      else indices.push(base + tris[i]!, base + tris[i + 1]!, base + tris[i + 2]!);
    }
    for (const loop of loops) {
      for (let k = 0; k < loop.length; k++) {
        edges.push(...place(frame, loop[k]!), ...place(frame, loop[(k + 1) % loop.length]!));
      }
    }
  }
}

function pushQuad(
  a: V3, b: V3, c: V3, d: V3,
  positions: number[], normals: number[], indices: number[],
): void {
  const n = triNormal(a, b, c) ?? triNormal(a, c, d);
  if (!n) return;
  const base = positions.length / 3;
  for (const v of [a, b, c, d]) {
    positions.push(...v);
    normals.push(...n);
  }
  if (triNormal(a, b, c)) indices.push(base, base + 1, base + 2);
  if (triNormal(a, c, d)) indices.push(base, base + 2, base + 3);
}

function triNormal(a: V3, b: V3, c: V3): V3 | null {
  const n = cross([b[0] - a[0], b[1] - a[1], b[2] - a[2]], [c[0] - a[0], c[1] - a[1], c[2] - a[2]]);
  const len = Math.hypot(...n);
  return len < 1e-10 ? null : [n[0] / len, n[1] / len, n[2] / len];
}

function isSharp(p0: Vec2, p1: Vec2, p2: Vec2): boolean {
  const l0 = Math.hypot(p1[0] - p0[0], p1[1] - p0[1]) || 1;
  const l1 = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) || 1;
  const cosA =
    ((p1[0] - p0[0]) / l0) * ((p2[0] - p1[0]) / l1) +
    ((p1[1] - p0[1]) / l0) * ((p2[1] - p1[1]) / l1);
  return cosA < Math.cos(Math.PI / 6);
}

const cross = (a: V3, b: V3): V3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const norm = (v: V3): V3 => {
  const l = Math.hypot(...v) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};

function rotateAbout(v: V3, axis: V3, angle: number): V3 {
  // Rodrigues' rotation formula.
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const k = axis;
  const kxv = cross(k, v);
  const kdv = dot(k, v);
  return [
    v[0] * c + kxv[0] * s + k[0] * kdv * (1 - c),
    v[1] * c + kxv[1] * s + k[1] * kdv * (1 - c),
    v[2] * c + kxv[2] * s + k[2] * kdv * (1 - c),
  ];
}
